import React, { useCallback } from 'react';
import { Grid } from "@mui/material"
import { IMfgSite } from "helpers/interfaces";
import { useTranslations } from "@fenetech/translations";
import { useFormContext } from "react-hook-form";
import CheckBoxForm from "components/Common/CheckBoxForm";
import AutocompleteMultiSelectForm from "components/Common/AutocompleteMultiSelectForm";

interface IProps {
    mfgSites: IMfgSite[] | null,
    requeryAssignableCustomers: (isAllSites: boolean, assignedSiteIDs: string[]) => Promise<void>,
    allSitesVisible: boolean,
    isVisible: boolean,
    readOnly: boolean
}

const MfgSiteSelect: React.FC<IProps> = ({ mfgSites, requeryAssignableCustomers, allSitesVisible, isVisible, readOnly }: IProps) => {

    const tm = useTranslations();
    const { watch, clearErrors } = useFormContext();

    const allSitesChecked = watch("isAllSites");
    const assignedSiteIDs = watch("siteIDs");

    const validateValue = React.useCallback((assignedSiteIDs: string[]): string | boolean => {
        if (isVisible && !allSitesChecked && assignedSiteIDs.length === 0) {
            return tm.GetWithParams("{0} cannot be blank.", tm.Get("Mfg Site"))
        }
        return true;
    }, [isVisible, allSitesChecked, tm]);

    const onAllSitesChange = useCallback((_, isAllSites) => {
        if (isAllSites) {
            // validation will not rerun on the autocomplete, so clear out errors in case they exist
            clearErrors("siteIDs");
        }
        requeryAssignableCustomers(isAllSites, assignedSiteIDs);
    }, [requeryAssignableCustomers, assignedSiteIDs, clearErrors]);

    const onMfgSitesChange = useCallback((assignedSites: IMfgSite[]) => {
        requeryAssignableCustomers(allSitesChecked, assignedSites.map(site => site.siteID));
    }, [requeryAssignableCustomers, allSitesChecked]);

    return <>
        <Grid item sx={{ display: allSitesVisible ? "flex" : "none" }} xs={12}>
            <CheckBoxForm
                fieldName="isAllSites"
                label={tm.Get("All Sites")}
                readOnly={readOnly}
                onChange={onAllSitesChange}
            />
        </Grid>
        <Grid item sx={{ display: isVisible ? "flex" : "none" }} xs={12}>
            <AutocompleteMultiSelectForm
                dataSource={mfgSites ?? []}
                isAllSelected={allSitesChecked}
                fieldName="siteIDs"
                valueField="siteID"
                displayField="siteName"
                translateDisplayField={true}
                label="Assigned Mfg Sites"
                readOnly={readOnly}
                size="medium"
                validateValue={validateValue}
                onChangeCallback={onMfgSitesChange}
            />
        </Grid>
    </>
}

export default MfgSiteSelect;